import {
  Box,
  Chip,
  Grid2 as Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { FC, useMemo } from "react";
import {
  useFormContext,
  Controller,
  useWatch,
  FieldErrors,
} from "react-hook-form";
import { useGetPartnerDropdownsQuery } from "src/shared/api/dropdowns/partner";
import {
  ContentAccordion,
  NumberTextField,
  AutocompleteField,
  PhoneField,
} from "src/shared/components";
import { ECountries } from "src/shared/types/common.types";
import { EOrganizationValue } from "src/shared/types/companies.types";
import { regexps, validatePhone } from "src/shared/utils/hookFormRules";
import { messages } from "src/shared/utils/hookFormRules/messages";
import {
  getOgrnRules,
  getOkpoAccountRules,
  getRegistrationNumberRules,
} from "src/shared/utils/hookFormRules/rules";
import { useGetLegalEntityInfo } from "../../model/hooks/useGetLegalEntityInfo";
import { IPartnerFormValues } from "../../model/types";
import {
  IEditPartnerFormValues,
  IPartnerBaseFormValues,
} from "../../model/types/partner";
import { License } from "./License";

export const RegistrationDetails: FC<{ isEdit?: boolean }> = ({
  isEdit = false,
}) => {
  const { data: dropdowns } = useGetPartnerDropdownsQuery();
  const { ownership } = dropdowns || {};

  const {
    control,
    formState: { errors },
  } = useFormContext<IPartnerFormValues | IEditPartnerFormValues>();

  const { isLoading: isLegalEntityLoading, handleChangeInput } =
    useGetLegalEntityInfo();

  const validatingFields: Array<keyof FieldErrors<IPartnerBaseFormValues>> = [
    "registration_number",
    "ogrn",
    "ownership_id",
    "name",
    "okpo",
    "kpp",
  ];

  const hasErrors =
    !!errors.license ||
    validatingFields.some((field) => errors.partner?.[field] !== undefined);

  const registration_country_id = useWatch({
    control,
    name: "partner.registration_country_id",
  });
  const organizationForm = useWatch({
    control,
    name: "partner.organization_form",
  });

  const isByRegistration = registration_country_id === ECountries.BY;
  const isRuRegistration = registration_country_id === ECountries.RU;
  const isNotByAndNotRuRegistration = !isByRegistration && !isRuRegistration;
  const isLegalEntity = organizationForm === EOrganizationValue.LegalEntity;

  const isRegistrationNumberReadOnly = isEdit;

  const ogrnRules = useMemo(
    () => getOgrnRules(organizationForm),
    [organizationForm],
  );

  const okpoRules = useMemo(
    () => getOkpoAccountRules(organizationForm, registration_country_id),
    [registration_country_id, organizationForm],
  );

  const registrationNumberRules = useMemo(
    () =>
      getRegistrationNumberRules(
        organizationForm || EOrganizationValue.Individual,
        registration_country_id,
      ),
    [organizationForm, registration_country_id],
  );

  return (
    <ContentAccordion
      title={
        <Box display="flex" alignItems="center">
          <Chip label="2" />
          <Typography
            color={hasErrors ? "error" : "grey.500"}
            sx={{ ml: 2 }}
            component="span"
          >
            Сведения о регистрации
          </Typography>
        </Box>
      }
    >
      <Stack direction="column" spacing={4}>
        <Box>
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
              <Controller
                control={control}
                name="partner.registration_number"
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <TextField
                    disabled={isLegalEntityLoading}
                    fullWidth
                    {...field}
                    onChange={(v) => {
                      field.onChange(v);
                      handleChangeInput(v.target.value);
                    }}
                    inputRef={ref}
                    label={
                      isByRegistration
                        ? "УНП"
                        : isRuRegistration
                          ? "ИНН"
                          : "Регистрационный номер"
                    }
                    error={!!error}
                    helperText={error?.message}
                    slotProps={{
                      input: {
                        readOnly: isRegistrationNumberReadOnly,
                      },
                      htmlInput: {
                        maxLength: isNotByAndNotRuRegistration ? 30 : undefined,
                      },
                    }}
                  />
                )}
                rules={registrationNumberRules}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <Controller
                control={control}
                name="partner.ownership_id"
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <AutocompleteField<number | "", false>
                    fullWidth
                    inputRef={ref}
                    {...field}
                    label="Форма собственности"
                    error={!!error}
                    helperText={error?.message}
                    options={ownership || []}
                    disabled={!isLegalEntity}
                  />
                )}
                rules={{
                  required:
                    isLegalEntity && !isNotByAndNotRuRegistration
                      ? messages.required
                      : undefined,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Controller
                control={control}
                name="partner.name"
                rules={{
                  required: messages.required,
                  validate: (value) =>
                    !/[«»]/.test(value) || "Символы «» недоступны",
                }}
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <TextField
                    fullWidth
                    {...field}
                    inputRef={ref}
                    label={isLegalEntity ? "Наименование" : "ФИО"}
                    error={!!error}
                    helperText={error?.message}
                    slotProps={{
                      htmlInput: {
                        maxLength: 64,
                      },
                    }}
                  />
                )}
              />
            </Grid>
            {!isNotByAndNotRuRegistration && (
              <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
                <Controller
                  control={control}
                  name="partner.okpo"
                  render={({
                    field: { ref, ...field },
                    fieldState: { error },
                  }) => (
                    <NumberTextField
                      fullWidth
                      allowLeadingZeros
                      {...field}
                      onChange={(v) => field.onChange(String(v))}
                      inputRef={ref}
                      label="ОКПО"
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                  rules={okpoRules}
                />
              </Grid>
            )}
            {isRuRegistration && (
              <>
                <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
                  <Controller
                    control={control}
                    name="partner.kpp"
                    render={({
                      field: { ref, ...field },
                      fieldState: { error },
                    }) => (
                      <NumberTextField
                        fullWidth
                        {...field}
                        onChange={(v) => field.onChange(String(v))}
                        inputRef={ref}
                        label="КПП"
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                    rules={{
                      validate: (val: string) => {
                        if (!val) return true;

                        return /^[0-9]{9}$/.test(val) || "9 цифр";
                      },
                    }}
                  />
                </Grid>
                {isRuRegistration && (
                  <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
                    <Controller
                      control={control}
                      name="partner.ogrn"
                      render={({
                        field: { ref, ...field },
                        fieldState: { error },
                      }) => (
                        <NumberTextField
                          fullWidth
                          {...field}
                          onChange={(v) => field.onChange(String(v))}
                          inputRef={ref}
                          label="ОГРН"
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                      rules={ogrnRules}
                    />
                  </Grid>
                )}
              </>
            )}
            {isNotByAndNotRuRegistration && (
              <>
                <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
                  <Controller
                    control={control}
                    name="partner.nonresident_phone"
                    render={({
                      field: { ref, onChange, ...field },
                      fieldState: { error },
                    }) => (
                      <PhoneField
                        inputRef={ref}
                        onChange={(_fieldValue, info) => {
                          onChange(info);
                        }}
                        {...field}
                        label="Телефон"
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                    rules={{
                      validate: (fieldValue) => validatePhone(fieldValue),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
                  <Controller
                    control={control}
                    name="partner.nonresident_email"
                    render={({
                      field: { ref, ...field },
                      fieldState: { error },
                    }) => (
                      <TextField
                        inputRef={ref}
                        fullWidth
                        {...field}
                        label="E-mail"
                        error={!!error}
                        helperText={error?.message}
                        slotProps={{
                          htmlInput: {
                            maxLength: 150,
                          },
                        }}
                      />
                    )}
                    rules={{
                      pattern: {
                        value: regexps.emailRegexp,
                        message: messages.email,
                      },
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Box>
        {!isNotByAndNotRuRegistration && (
          <License isEdit={isEdit && isByRegistration} />
        )}
      </Stack>
    </ContentAccordion>
  );
};
